import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { profileService } from '@/shared/api/services/profile.service';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import {
  ACTIVE_SUBSCRIPTION_DEFAULT,
  getActiveSubscriptionSnapshot,
  type ActiveSubscriptionData,
  type EffectivePlan,
} from './active-subscription.query';
import { useSubscriptionNotificationsStore } from './subscription-notifications.store';
import {
  useTokenBalance as useTokenBalanceQuery,
  useTokenBalanceInitialized as useTokenBalanceInitializedQuery,
} from '@/shared/api/hooks/use-token-balance';
import { isSubscriptionGrantingAccess } from '@/features/subscription/lib/trial-helpers';
import type {
  Plan,
  PlanResource,
  PaywallPayloadKey,
  PaywallPayloadMap,
  SubscriptionResource,
} from '@/types/subscription';
import type { PaywallPayload } from '@/shared/lib/api';

// SubscriptionLimits + helper computations live alongside selectors because
// they're pure derivations from the Query payload — they used to live in the
// old Zustand store.
export interface SubscriptionLimits {
  generations_queue: number;
  projects: number;
  angles: number;
  /** Per-project library limit (computed by API as angles × multiplier). */
  project_angles: number;
  references: number;
  simultaneous_references: number;
  speed_multiplier: number;
  has_remove_watermark: boolean;
  has_output_style: boolean;
  // Renamed from `has_high_resolution`. Derived from the plan flag
  // `is_feature_upscale` — gates the post-render 4K Replicate upscale flow.
  has_upscale: boolean;
  // Pro+ feature flag for native ~2K rendering (the in-form `is_hires`
  // toggle). Distinct from `has_upscale`.
  has_hires_render: boolean;
  // Pro+ feature flag for stronger reasoning model on the design prompt
  // (the in-form `is_extended_thinking` toggle).
  has_extended_thinking: boolean;
  is_white_label_allowed: boolean;
  has_model_selection: boolean;
  // Pro+ feature flag for the AI cost-estimate flow (the Estimate design tab).
  // Derived from the plan flag `is_feature_cost_estimate`.
  has_cost_estimate: boolean;
  // Builder Pass / grandfathered Pro/Designer flag: surfaces the manual engine
  // knobs (Effort, Model, Extended thinking, Hi-Res). Derived from the plan
  // flag `is_advanced_render_controls`. Free + one-time pack buyers read false
  // and never see those rows.
  has_advanced_render_controls: boolean;
}

function getPlanAttrs(plan: Plan | PlanResource | null) {
  if (!plan) return null;
  return 'attributes' in plan ? plan.attributes : plan;
}

// Re-export so consumers can keep one import path through `model/`.
export type { EffectivePlan };

/**
 * Read the active subscription bundle from the Query cache.
 *
 * - Authority order: auth flow (`applyProfileData`) seeds via `setQueryData`,
 *   then WS `users.ts` pushes deltas the same way. This hook's `queryFn`
 *   only covers the cold-start path when a UI tree mounts before auth
 *   seeders fire.
 * - Guest gate: `queryFn` returns the default bundle when no token exists,
 *   so guests don't 401 on `/v1/user`. We can't use `enabled` keyed off
 *   `useAuthStore` here because that would create an import cycle through
 *   `auth.store → websocket → use-queue-limit → subscription.selectors`.
 * - `staleTime: Infinity` — WS is the live channel; auto-refetch would race.
 *   In practice `AuthInitializer` calls `applyProfileData` early enough that
 *   `queryFn` only fires for guests / fully-unmounted-then-remounted cases.
 */
export function useActiveSubscription() {
  return useQuery<ActiveSubscriptionData>({
    queryKey: cacheKeys.subscriptions.active,
    queryFn: async () => {
      if (!AuthTokenService.getTokenSync()) {
        return ACTIVE_SUBSCRIPTION_DEFAULT;
      }
      const profile = await profileService.fetchProfile();
      return profileService.transformSubscriptionData(profile);
    },
    staleTime: Infinity,
  });
}

// Simple selectors for direct state access
export const useSubscription = (): SubscriptionResource | null =>
  useActiveSubscription().data?.subscription ?? null;

export const usePlan = (): Plan | PlanResource | null =>
  useActiveSubscription().data?.plan ?? null;

export const useUsage = () => useActiveSubscription().data?.usage ?? null;

// Guest mode selectors — default to `true` until the profile is seeded so
// gates stay closed during cold start.
export const useIsGuestMode = (): boolean =>
  useActiveSubscription().data?.isGuestMode ?? true;

export const useEffectivePlan = (): EffectivePlan | null =>
  useActiveSubscription().data?.effectivePlan ?? null;

// Token balance + initialized flag are owned by the balance Query; re-exported
// here so subscription consumers keep one import path.
export const useTokenBalance = useTokenBalanceQuery;
export const useTokenBalanceInitialized = useTokenBalanceInitializedQuery;

// Loading + error now derive from Query state.
export const useSubscriptionLoading = (): boolean =>
  useActiveSubscription().isLoading;
export const useSubscriptionError = (): string | null => {
  const error = useActiveSubscription().error;
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
};

export const useSubscriptionNotifications = () =>
  useSubscriptionNotificationsStore((state) => state.notifications);

/**
 * Cold-start gate: have we seeded both the subscription bundle AND the token
 * balance? Consumers fall back to safe defaults until this returns true.
 */
export const useIsDataLoaded = () => {
  const tokenInitialized = useTokenBalanceInitializedQuery();
  const data = useActiveSubscription().data;
  return tokenInitialized && data !== undefined;
};

// Renderability is NOT decided client-side. The server emits the single
// authoritative gate via the queue aggregate's `block_reason` (see
// `useRenderBlockReason` + `use-render-preflight.ts`). There is intentionally
// no `useCanGenerate` / `useHasGenerationAccess` here: a client `tokenBalance
// > 0` / `requires subscription` check wrongly blocked free/guest users that
// the server (post free-tier pivot) allows to render.

export const useRemainingGenerations = (): number => -1;

export const useRemainingTokens = (): number => useTokenBalanceQuery();

export const useSubscriptionLimits = (): SubscriptionLimits | null => {
  const plan = usePlan();
  return useMemo(() => computeLimits(plan), [plan]);
};

/**
 * Preflight paywall payloads keyed by typed limit code. Populated from the
 * plan resource (`attributes.paywall_payloads`). Clients use this for the
 * cached "limit reached" path so it renders the same server-owned copy the
 * 422 response would carry. Returns undefined during cold start.
 */
export const usePaywallPayloads = (): PaywallPayloadMap | undefined => {
  const plan = usePlan();
  return useMemo(() => {
    const attrs = getPlanAttrs(plan);
    return attrs?.paywall_payloads;
  }, [plan]);
};

export const usePaywallPayload = (
  key: PaywallPayloadKey
): PaywallPayload | undefined => {
  const payloads = usePaywallPayloads();
  return payloads?.[key];
};

// Combined selectors for common use cases
export const useSubscriptionStatus = () => {
  const data = useActiveSubscription().data;
  return useMemo(() => {
    const subscription = data?.subscription ?? null;
    const planAttrs = getPlanAttrs(data?.plan ?? null);
    const subscriptionData =
      subscription && 'attributes' in subscription
        ? subscription.attributes
        : subscription;

    const isActive = isSubscriptionGrantingAccess(
      subscription && 'attributes' in subscription ? subscription : null
    );

    return {
      isActive,
      status: subscriptionData?.status || null,
      planName: planAttrs?.name || null,
    };
  }, [data]);
};

/**
 * Action surface — only the client-owned notifications APIs survive.
 * Server-state mutations (setSubscriptionData/setLoading/setError) are
 * gone: the active-subscription Query is the writer.
 */
export const useSubscriptionActions = () => {
  const addNotification = useSubscriptionNotificationsStore(
    (state) => state.addNotification
  );
  const dismissNotification = useSubscriptionNotificationsStore(
    (state) => state.dismissNotification
  );
  return { addNotification, dismissNotification };
};

// Helper hooks for specific features
export const useHasFeature = (feature: keyof SubscriptionLimits): boolean => {
  const limits = useSubscriptionLimits();
  return Boolean(limits?.[feature] ?? false);
};

export const useIsSubscriptionActive = (): boolean => {
  const subscription = useSubscription();
  return isSubscriptionGrantingAccess(
    subscription && 'attributes' in subscription ? subscription : null
  );
};

// Helper function for checking limits
export const useCheckLimit = () => {
  const limits = useSubscriptionLimits();

  return useMemo(
    () =>
      (limitType: keyof SubscriptionLimits, currentCount: number = 0) => {
        if (!limits) return false;

        const limit = limits[limitType];
        if (typeof limit === 'boolean') return limit;
        if (typeof limit === 'number') return currentCount < limit;
        return false;
      },
    [limits]
  );
};

// Helper function for checking if upgrade is needed
export const useNeedsUpgrade = () => {
  const limits = useSubscriptionLimits();

  return useMemo(
    () => (feature: string) => {
      if (!limits) return true;

      const featureMap: { [key: string]: keyof SubscriptionLimits } = {
        output_style: 'has_output_style',
        upscale: 'has_upscale',
        hires_render: 'has_hires_render',
        extended_thinking: 'has_extended_thinking',
        remove_watermark: 'has_remove_watermark',
      };

      const limitKey = featureMap[feature];
      if (!limitKey) return false;

      return !limits[limitKey];
    },
    [limits]
  );
};

// Plan tier helper
export const usePlanTier = () => {
  const plan = usePlan();
  const planAttrs = getPlanAttrs(plan);
  const planName = planAttrs?.name?.toLowerCase() || 'lite';

  return useMemo(
    () => ({
      planName,
      isLite: planName === 'lite',
      isPro: planName === 'pro',
      isMax: planName === 'max',
      isFreeTrial: planName === 'free_trial',
    }),
    [planName]
  );
};

// --- pure computations (used outside React) -------------------------------

export const computeLimits = (
  plan: Plan | PlanResource | null
): SubscriptionLimits | null => {
  const planData = getPlanAttrs(plan);
  if (!planData) return null;

  return {
    generations_queue: planData.limit_generations_queue || 0,
    projects: planData.limit_projects || 0,
    angles: planData.limit_angles || 0,
    project_angles:
      planData.limit_project_angles ?? (planData.limit_angles || 0) * 3,
    references: planData.limit_references || 0,
    // Uniform per-category cap of 3 across ALL plans/Passes — the server
    // enforces `max:3` everywhere and is NOT a paid differentiator. Fall back
    // to 3 (not 1) so guests / Pass-only / no-subscription users get the real
    // cap, matching server + Apple + Android. A `1` fallback wrongly capped
    // them at 1 reference per category.
    simultaneous_references: planData.limit_simultaneous_references || 3,
    speed_multiplier: planData.speed_multiplier || 1,
    has_remove_watermark: planData.is_feature_remove_watermark || false,
    has_output_style: planData.is_feature_output_style || false,
    has_upscale: planData.is_feature_upscale || false,
    has_hires_render: planData.is_feature_hires_render || false,
    has_extended_thinking: planData.is_feature_extended_thinking || false,
    is_white_label_allowed: planData.is_white_label_allowed || false,
    has_model_selection: planData.is_feature_model_selection || false,
    has_cost_estimate: planData.is_feature_cost_estimate || false,
    has_advanced_render_controls: planData.is_advanced_render_controls || false,
  };
};

// Non-React snapshot helper for callers that need the bundle synchronously
// (e.g. `useLimitErrorPaywall`).
export { getActiveSubscriptionSnapshot };
